// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquarePlus, RefreshCw, Search, Loader2, Image as ImageIcon, ArrowRight, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { FeedbackDrawer, StatusPill, fmtDT, evText, IdChip } from "../../components/FeedbackButton";

/**
 * AdminFeedback —— 系统管理 →「需求反馈」汇总页（仅 admin / ceo）。
 *
 * 需求来源（2026-10-14，见 backend/feedback.py 文件头）：
 *   汇总所有用户提交的反馈 / Bug，做成一个列表，支持：
 *     · 状态筛选（全部 / 待处理 / 已解决 / 不解决）与关键字搜索；
 *     · 点条目开右侧抽屉看详情（问题原因 / 截图 / 提交人账号与登录凭证）；
 *     · 在抽屉里做「已解决 / 不解决 / 待处理」状态流转，以及与提交人来回回复（聊天记录）。
 *
 * 数据来源：GET /admin/feedback（列表 + 计数）、GET /admin/feedback/unread（侧栏角标）。
 * 打开详情即清零该条未读（后端在 GET /feedback/{id} 里处理），随后派发 feedback-changed
 * 事件让顶栏/侧栏角标立即刷新。
 *
 * 追加（2026-10-14，管理员反馈「看不出哪条有新消息 / 别人来了新消息我不知道」）：
 *   · 列表项新增「最近动态」一行，写清是「XX 有新留言」还是「状态变更 x→x」，并带动态时间；
 *   · 有未读的条目**整行浅红底** + 红点 + 未读数字，扫一眼就知道该处理哪条；
 *   · 本页每 20s **静默自动刷新**（不动 busy、不闪 loading），新动态自动出现。
 *   视角说明：列表用 evText(..., "admin")，管理员看到 side="reporter" 的消息才叫「对方有新留言」。
 *
 * 追加（2026-10-15，用户需求「需求反馈列表和右上角的反馈面板列表里增加当前 bug 的 id 的显示」）：
 *   · 列表项 meta 行首位显示完整反馈 ID（复用 FeedbackButton 导出的 IdChip，点击可复制）；
 *   · 详情抽屉头部同样带 ID（在 FeedbackButton 内实现，本页无需额外处理）。
 *   ⚠️ 搜索框暂**不支持**按 ID 搜：后端 GET /admin/feedback 的 q 只匹配
 *      title / pageLabel / reporter.name|nameZh|username（见 feedback.py），这里不擅自改后端。
 *
 * 追加（2026-10-21，用户需求「做成分页列表；全部显示时处理中在最上面，然后待处理，
 * 然后不解决，然后已解决；今天还加的用户重新开启功能这个页面上也要体现」）：
 *   · 分页：后端 /admin/feedback 改为 page / pageSize 分页返回（total / totalPages / hasMore），
 *     本页底部加分页条。以前一次性拉 500 条，反馈攒多后首屏很慢且看不出总数。
 *     计数卡仍是全局统计，切页不影响。
 *   · 排序：状态为「全部」时，后端按「处理中 → 待处理 → 不解决 → 已解决」分组，
 *     组内按更新时间倒序；前端只负责展示，不在页内二次排序（分页后页内排序会打乱跨页顺序）。
 *   · 重新开启标识：提交人把已解决 / 不解决重开回来时，列表项显示品牌色「重新开启」
 *     徽章 + 累计次数，管理员一眼看出「这条是用户重新拉回队列的」，不会误判成数据异常。
 *
 * 追加（2026-10-23，用户反馈三件事「搜索不能按 ID 搜 / 没有分页 / 用户端的重新开启在这里看不到」）：
 *   ① **按 ID 搜索**：以前后端 q 只匹配标题 / 页面 / 提交人，把界面上「点击复制」的 ID
 *      粘进搜索框一条都搜不到。现由后端 `_search_filter` 支持完整 ID 精确命中 + ID 片段模糊命中，
 *      本页只负责把提示文案改清楚（让用户知道「ID 也能搜」）。
 *   ② **分页更显眼**：分页条原来只有「上一页 / 下一页」两个按钮，且压在 20 行列表最底部，
 *      很容易被当成「没有分页」。现在补成「页码按钮 + 省略号」的标准分页条，
 *      并把「共 N 条 · 第 x/y 页」同时提到列表**上方**工具条里 —— 不滚到底也能看出有多少页。
 *   ③ **重新开启在此页可见**：用户端有「重新开启」按钮，管理端以前只有列表项上一个不说话
 *      就消失的徽章，且线上没有重开数据时完全看不到这个能力。现在：
 *      · 筛选区新增「重新开启」筛选（带条数），一键只看被用户拉回队列的条目；
 *      · 列表项徽章保留；
 *      · 详情抽屉里管理员也能看到「提交人重开了 N 次 / 最近一次时间 / 原因」。
 *
 * ⚠️ 分页与静默刷新的配合：静默刷新只重拉**当前页**（保留 page），不能重置到第 1 页 ——
 *    否则管理员翻到第 3 页看条目，60 秒后会被自动刷新甩回首页，属于很恼人的体验回退。
 */
const TABS = [
  { v: "", zh: "全部", en: "All" },
  { v: "pending", zh: "待处理", en: "Pending" },
  { v: "processing", zh: "处理中", en: "In progress" },
  { v: "resolved", zh: "已解决", en: "Resolved" },
  { v: "wontfix", zh: "不解决", en: "Won't fix" },
  // 「重新开启」是**视图**而不是另一种状态（2026-10-23 用户纠正）：
  //   提交人重开后状态本来就是「待处理」，它没有独立的状态码。
  //   但管理员需要一个「只看被用户重开、还没再次收尾」的入口，
  //   所以把它做成与状态并列的一档视图 —— 视觉上必须和其它状态同一个 tab 组，
  //   不能长成一个游离在筛选栏外面的独立按钮（那是上一版的做法，用户反馈「为什么分离了」）。
  //   ⚠️ 它与状态是**互斥**的：选中它时不再额外叠加某个状态 tab（见 params 里的口径）。
  { v: "reopened", zh: "重新开启", en: "Reopened" },
];

/**
 * 计算分页条要渲染的页码（2026-10-23）。
 *
 * 为什么不能直接 1..totalPages：反馈攒到几百条时会有几十页，全渲染既挤爆一行
 * 也没有可读性。这里采用常见做法 —— 首尾页 + 当前页左右各 1 页，中间用省略号折叠。
 * 边界处理（当前页在首/尾附近）靠 Set 去重后再排序，避免出现「1 … 2 3 4 … 10」这种空折叠。
 *
 * @param {number} current 当前页（1 起）
 * @param {number} total   总页数
 * @returns {Array<number|"…">} 例如 [1, "…", 4, 5, 6, "…", 20]
 */
function pageItems(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  const nums = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
}

// 每页条数：20 与项目里其它分页列表（使用分析时间线、已签约客户滚动分页）保持同一档。
const PAGE_SIZE = 20;

export default function AdminFeedback() {
  const { lang, api } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);

  const [d, setD] = useState(null);
  // tab 是**单一维度**的当前视图（2026-10-23 用户纠正后合并）：
  //   取值 = "" | "pending" | "processing" | "resolved" | "wontfix" | "reopened"。
  //   上一版把「重新开启」单独做成一个 boolean（reopened）和一个游离按钮，导致
  //   它既在视觉上和状态 tab 分离、又能与某个状态 tab 同时生效（例如「已解决 + 重新开启」
  //   这个自相矛盾的组合）。合并成一个枚举后，这种矛盾状态在类型上就不可能出现。
  const [tab, setTab] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // 请求参数统一在这里拼：视图 / 关键字 / 页码 三者口径只写一处，
  // 避免 load 与 silentLoad 各拼一套、日后加了筛选条件只改了一边。
  // ⚠️ 「重新开启」不是状态码，走独立参数 reopened=1；其余取值原样作为 status 传给后端。
  const params = useCallback((targetPage = page) => ({
    status: tab === "reopened" ? "" : tab,
    q, page: targetPage, pageSize: PAGE_SIZE,
    reopened: tab === "reopened" ? 1 : "",
  }), [tab, q, page]);

  const load = useCallback(async (targetPage = page) => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/feedback", { params: params(targetPage) });
      // 后端会把越界页码夹回有效范围（删到最后一页只剩几条时，翻页可能超出）；
      // 这里以**服务端返回的 page** 为准回写，避免前端页码与内容错位。
      setD(data);
      setPage(data?.page || 1);
    }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  }, [api, params, page]);
  // 切 tab / 改搜索 / 切换重开筛选都回到第 1 页（对齐原行为：筛选条件一变就重新查）。
  // 不能只是 setPage(1) —— 页码变化不在这个 effect 的依赖里，得显式按第 1 页请求。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [tab, q]);

  /**
   * 静默自动刷新（需求 2026-10-14：管理员反馈「别人来了新消息我不知道」）。
   *
   * 原来的列表只在「挂载 / 改筛选 / 改搜索 / 自己操作后」才拉一次 —— 管理员停在
   * 这个页面时，别人新提交的反馈、新回复都不会自己出现，必须手动点「刷新」。
   * 现在每 60s 静默拉一次（**不动 busy**，避免每 60s 整页闪一下 loading），
   * 新动态会自动出现在列表里并整行标红。
   *
   * ⚠️ 间隔取 60s 而不是更短：用户明确说「不用那么实时」，且 Layout 里侧栏角标
   *    （/admin/feedback/unread）本来就是 60s 轮询，两处节奏对齐口径统一。
   * ⚠️ 必须带上**当前页码**（2026-10-21 加分页后）：不传 page 会回到第 1 页，
   *    管理员翻到第 3 页时会被 60s 定时器甩回首页。
   */
  const silentLoad = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/feedback", { params: params(page) });
      setD(data);
      setPage(data?.page || 1);
    }
    catch { /* 静默刷新失败不打扰用户，下一轮再试 */ }
  }, [api, params, page]);
  useEffect(() => {
    const t = setInterval(silentLoad, 60000);
    return () => clearInterval(t);
  }, [silentLoad]);

  const rows = useMemo(() => d?.items || [], [d]);
  const counts = d?.counts || {};
  const totalPages = d?.totalPages || 1;
  const total = d?.total ?? 0;
  // 切 tab 要回到第 1 页：否则停在第 5 页切到只有 2 条的 tab 会看到空列表，
  // 用户会以为「这个状态下没有反馈」，其实只是页码越界。
  // （搜索沿用原行为：随输入即时查询，不改成回车才查 —— 免得用户以为输入没生效。）
  const changeTab = (v) => { setTab(v); setPage(1); };
  const reopenedCount = counts.reopened ?? 0;
  // 当前是否有任何筛选生效：空列表时据此区分「一条反馈都没有」与「筛选无结果」（Skill 要求）
  const filtered = !!tab || !!q.trim();

  return (
    <div className="space-y-4" data-testid="admin-feedback">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2">
          <MessageSquarePlus className="h-5 w-5 text-brand-600" />{T("需求反馈", "Feedback")}
        </h1>
        <p className="text-xs text-slate-500">{T("汇总用户提交的需求反馈 / Bug，可标记状态并与提交人回复。", "All user feedback and bug reports; mark status and reply.")}</p>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" data-testid="admin-feedback-refresh" onClick={() => load(page)} disabled={busy} className="gap-1">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />{T("刷新", "Refresh")}
          </Button>
        </div>
      </div>

      {/* 状态计数（2026-10-15 增加「处理中」后由 5 格变 6 格，2026-10-23 增加「重新开启」后 7 格）
          ⚠️ 这里是**全局**统计，不随 tab / 搜索 / 分页变化（见后端 admin_list 注释）。
          2026-10-23 用户纠正：上一版把「重新开启」这张卡做成可点的筛选开关，
          既和状态 tab 分离、又让「卡片可点、别的不行」显得不一致。
          现在计数卡全部**只做统计**（不可点），筛选入口统一收在上面那排 tab 里。 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="admin-feedback-counts">
        {[["all", T("全部", "All"), "text-slate-800"], ["pending", T("待处理", "Pending"), "text-amber-700"],
          ["processing", T("处理中", "In progress"), "text-sky-700"],
          ["resolved", T("已解决", "Resolved"), "text-emerald-700"], ["wontfix", T("不解决", "Won't fix"), "text-slate-600"],
          // 「重新开启」计数（2026-10-23）：提交人拉回队列、尚未再次收尾的条数。
          // 用户反馈「用户端有重新开启按钮，但这里看不到」，所以给它一个可见的数字。
          ["reopened", T("重新开启", "Reopened"), "text-brand-700"],
          ["unread", T("未读", "Unread"), "text-rose-600"]].map(([k, l, cls]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`admin-feedback-count-${k}`}>
            <p className="text-[11px] text-slate-400">{l}</p>
            <p className={`text-2xl font-bold ${cls}`}>{counts[k] ?? 0}</p>
          </div>
        ))}
      </div>

      {/* 筛选 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-xl border border-slate-200 bg-white p-0.5" role="tablist"
          aria-label={T("按视图筛选", "Filter by view")}>
          {/* 「重新开启」与状态 tab 同组排布（2026-10-23 用户纠正「应该是一个状态，为什么分离了」）。
              它在数据上不是一个 status 码，但在「我要看哪一批」这件事上和状态 tab 是同一维度，
              所以视觉上必须是同一排里的一档，而不是外面一个孤立的按钮。 */}
          {TABS.map((tb) => {
            const on = tab === tb.v;
            return (
              <button key={tb.v} type="button" role="tab" aria-selected={on}
                data-testid={`admin-feedback-tab-${tb.v || "all"}`} onClick={() => changeTab(tb.v)}
                className={"max-md:min-h-11 max-md:min-w-11 inline-flex items-center gap-1 " +
                  (`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${on ? "bg-brand-500 text-white" : "text-slate-500 hover:text-slate-800"}`)}>
                {/* 重开这一档带个图标，和安全/状态词在视觉上稍作区分，但仍属同一组 */}
                {tb.v === "reopened" && <RotateCcw className="h-3 w-3" />}
                {zh ? tb.zh : tb.en}
                {/* 条数只用来说明「这档有多少」，不承担筛选入口的作用 */}
                {tb.v === "reopened" && reopenedCount > 0 && (
                  <span className={`tabular-nums rounded-full px-1.5 text-[10px] ${on ? "bg-white/25 text-white" : "bg-slate-100 text-slate-600"}`}>{reopenedCount}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="admin-feedback-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
            // 提示文案带上「反馈 ID」（2026-10-23）：以前只写问题描述 / 提交人 / 页面，
            // 用户根本不知道 ID 也能搜，而 ID 恰恰是查库 / 沟通时最常用的检索依据。
            placeholder={T("搜索问题描述 / 提交人 / 页面 / 反馈 ID…", "Search description / reporter / page / ID…")} className="pl-8" />
        </div>
      </div>

      {/* 列表上方的一行摘要（2026-10-23）：原来「共 N 条 · 第 x / y 页」只在列表**底部**，
          20 行数据要滚到底才看得到，很容易被当成「这页没有分页」。
          这里把它提到列表上方，与筛选同一屏可见；底部仍保留完整分页条。 */}
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400" data-testid="admin-feedback-summary">
          <span className="tabular-nums">
            {zh ? `共 ${total} 条 · 第 ${page} / ${totalPages} 页` : `${total} items · page ${page}/${totalPages}`}
            {filtered && <span className="ml-2 text-brand-600">{T("（已筛选）", "(filtered)")}</span>}
          </span>
          <span className="tabular-nums">{zh ? `本页 ${rows.length} 条` : `${rows.length} on this page`}</span>
        </div>
      )}

      {/* 列表 */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {busy && !d ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          /* 空状态区分「一条都没有」与「筛选无结果」（2026-10-23，Skill 要求）：
             以前两种情况都写「暂无反馈」，用户筛了条件后看到它，会以为数据丢了。 */
          <div className="p-10 text-center text-sm text-slate-300" data-testid="admin-feedback-empty">
            {filtered ? (
              <div className="space-y-2">
                <p>{T("没有符合筛选条件的反馈", "No feedback matches these filters")}</p>
                <button type="button" data-testid="admin-feedback-clear-filters"
                  onClick={() => { setTab(""); setQ(""); setPage(1); }}
                  className="text-xs font-semibold text-brand-600 hover:underline">
                  {T("清除筛选条件", "Clear filters")}
                </button>
              </div>
            ) : (
              <p>{T("暂无反馈", "No feedback yet")}</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((f) => (
              /* 未读行整行浅红底 + 红点，一眼看出「哪条有新消息」（需求 2026-10-14：
                 管理员反馈「在列表上看不出哪条有新消息」）。点开详情即清零 adminUnread。 */
              <button key={f.id} data-testid={`admin-feedback-row-${f.id}`} onClick={() => setDetailId(f.id)}
                className={"max-md:min-h-11 max-md:min-w-11 " + (`w-full text-left px-4 py-3 flex items-start gap-3 ${f.adminUnread > 0 ? "bg-rose-50/60 hover:bg-rose-50" : "hover:bg-brand-50/40"}`)}>
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${f.adminUnread > 0 ? "bg-rose-500" : "bg-transparent"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{f.title}</span>
                    {f.adminUnread > 0 && <span className="shrink-0 h-4 min-w-4 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center font-bold">{f.adminUnread}</span>}
                    {/* 重新开启徽章（2026-10-21 需求）：提交人把已解决 / 不解决拉回队列后，
                        当前状态是待处理 / 处理中，管理员会疑惑「这条不是处理过了吗」。
                        徽章只在「已重开且仍未被再次收尾」时出现，收尾后自然消失。 */}
                    {f.reopenedByReporter && (
                      <span data-testid={`admin-feedback-reopened-${f.id}`}
                        title={f.reopenedAt ? T(`提交人于 ${fmtDT(f.reopenedAt)} 重新开启`, `Reopened by reporter at ${fmtDT(f.reopenedAt)}`) : ""}
                        className="shrink-0 inline-flex items-center gap-0.5 rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 border border-brand-200">
                        <RotateCcw className="h-2.5 w-2.5" />{T("重新开启", "Reopened")}
                        {(f.reopenCount || 0) > 1 && <span className="tabular-nums">×{f.reopenCount}</span>}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-slate-500">
                    {/* 反馈 ID（需求 2026-10-15）：放在 meta 行首位当「标识列」，点击可复制。
                        整行是 <button>，IdChip 内部已 stopPropagation，点 ID 不会误开抽屉。 */}
                    <IdChip id={f.id} zh={zh} testId={`admin-feedback-id-${f.id}`} />
                    <span>{T("提交人", "By")}：{f.reporter?.nameZh || f.reporter?.name || f.reporter?.username || "-"}</span>
                    <span className="text-slate-400">{f.reporter?.username}</span>
                    {f.pageLabel && <span>{T("页面", "Page")}：{f.pageLabel}</span>}
                    <span>{fmtDT(f.createdAt)}</span>
                    {/* 附件数：优先用后端新给的 attachments（含留言里的附件）；
                        老字段 screenshots 只数「提交时附的那批」，回复里附的会漏掉。 */}
                    {(f.attachments ?? f.screenshots) > 0 && <span className="inline-flex items-center gap-0.5 text-slate-400"><ImageIcon className="h-3 w-3" />{f.attachments ?? f.screenshots}</span>}
                    <span className="text-slate-400">{f.messageCount} {T("条回复", "replies")}</span>
                  </div>
                  {/* 最近动态：直接写清这条是「提交人有新留言」还是「状态变更 x→x」，
                      未读时玫红加粗（与左侧红点/整行红底同一套「有未读」语义）。
                      视角传 admin —— 管理员看到 side=reporter 的消息才是「对方有新留言」。 */}
                  {f.lastEvent && (
                    <div data-testid={`admin-feedback-event-${f.id}`}
                      className={`mt-1 flex items-center gap-1 text-[11px] leading-snug ${f.adminUnread > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
                      {f.lastEvent.kind === "status" ? <ArrowRight className="h-3 w-3 shrink-0" />
                        : f.lastEvent.kind === "reopen" ? <RotateCcw className="h-3 w-3 shrink-0" />
                          : <MessageSquarePlus className="h-3 w-3 shrink-0" />}
                      <span className="truncate">{evText(f.lastEvent, zh, "admin")}</span>
                      <span className={`shrink-0 ${f.adminUnread > 0 ? "text-rose-400" : "text-slate-400"}`}>· {fmtDT(f.lastEvent.at)}</span>
                    </div>
                  )}
                </div>
                <StatusPill status={f.status} zh={zh} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 分页条（2026-10-21 需求「做成分页列表」；2026-10-23 补成页码形式）。
          总数放在左侧：管理员要能立刻知道「这类反馈一共多少条」，而不是只看到当前 20 条。
          ⚠️ rows.length === 0 时不渲染 —— 空列表下面挂一条「第 1 / 1 页」没有意义，
             只会让空状态显得更嘈杂。
          2026-10-23 用户反馈「没有做分页显示」：原先只有「上一页 / 下一页」两个按钮，
          当只有 2 页时两个按钮中间没有任何页码信息，几乎看不出这是分页控件。
          现在补成「‹ 1 2 … 20 ›」的页码形式，当前页高亮，页数多时中间折叠成省略号。
          按钮用 size="sm"，与项目里其它分页条（使用分析时间线）同一档视觉重量。 */}
      {rows.length > 0 && totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="admin-feedback-pager">
          <span className="text-[11px] text-slate-400 tabular-nums" data-testid="admin-feedback-pager-count">
            {zh ? `共 ${total} 条 · 第 ${page} / ${totalPages} 页` : `${total} items · page ${page}/${totalPages}`}
          </span>
          <nav className="flex items-center gap-1" aria-label={zh ? "分页" : "Pagination"}>
            <Button type="button" variant="outline" size="sm" className="h-7 gap-0.5 px-2 text-[11px]"
              disabled={page <= 1 || busy} onClick={() => load(page - 1)} data-testid="admin-feedback-prev">
              <ChevronLeft className="size-3.5" aria-hidden="true" />{zh ? "上一页" : "Prev"}
            </Button>
            {pageItems(page, totalPages).map((n, i) => (
              n === "…" ? (
                <span key={`gap-${i}`} aria-hidden="true" className="px-1 text-[11px] text-slate-300 select-none">…</span>
              ) : (
                <Button key={n} type="button" size="sm" disabled={busy}
                  variant={n === page ? "default" : "outline"}
                  onClick={() => { if (n !== page) load(n); }}
                  aria-current={n === page ? "page" : undefined}
                  data-testid={`admin-feedback-page-${n}`}
                  className={`h-7 min-w-7 px-2 text-[11px] tabular-nums ${n === page ? "bg-brand-500 text-white border-brand-500 hover:bg-brand-600" : ""}`}>
                  {n}
                </Button>
              )
            ))}
            <Button type="button" variant="outline" size="sm" className="h-7 gap-0.5 px-2 text-[11px]"
              disabled={page >= totalPages || busy} onClick={() => load(page + 1)} data-testid="admin-feedback-next">
              {zh ? "下一页" : "Next"}<ChevronRight className="size-3.5" aria-hidden="true" />
            </Button>
          </nav>
        </div>
      )}

      {detailId && (
        <FeedbackDrawer fid={detailId} zh={zh} api={api} isAdmin
          onRead={() => window.dispatchEvent(new Event("feedback-changed"))}
          onClose={() => { setDetailId(null); load(); window.dispatchEvent(new Event("feedback-changed")); }}
          onStatusChanged={() => { load(); window.dispatchEvent(new Event("feedback-changed")); }} />
      )}
    </div>
  );
}
